import { Localization } from "../../OW.Remoting/Remoting/Localization";
import { GenericControl } from "../UI.BaseControls/GenericControl";
import { EventHelper } from "../UI.BaseControls/Helpers/EventHelper";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { FileEditStatus } from "../UI/UI/FileMenu";
import { FileEditElement } from "./FileEditElement";
import { IFileUploadStatusChangeEvent } from "./IEvent";
import { SimpleFileEditElement } from "./SimpleFileEditElement";

export class ImageEditElement extends HTMLElement {
    private _fileEdit: FileEditElement;
    private _imageElement: HTMLImageElement;
    private _previewContainer: HTMLElement;
    private _resizeBtn: HTMLElement;
    private _imageTestElement: HTMLImageElement;

    private _status: FileEditStatus;
    private _imgAddr: string;
    private _errorSrc: boolean; // pokud byl pri uploadu error, aby zustal i po dokonceni a uploadu a zmeny stavu na fileeditu

    //#region Properties
    public set visible(v: boolean) {
        this.classList.toggle("Hidden", !v);
    }

    public get visible(): boolean {
        return !this.classList.contains("Hidden");
    }

    public set enabled(v: boolean) {
        this._fileEdit.enabled = v;
        this._fileEdit.visible = v;
        this.classList.toggle("disabled", !v);
    }

    public get enabled(): boolean {
        return !this._fileEdit.enabled;
    }

    public set src(v: string) {
        this._imgAddr = v;
        this._errorSrc = false;
        if (v) {
            this._imageElement.style.backgroundImage = `url(${v})`; //`
            this._imageTestElement.src = v; // jen pro test, zda vlozeny obrazek je obrazek
            this.setStatus(FileEditStatus.download);
        } else {
            this._imageElement.style.backgroundImage = "";
            this.setStatus(FileEditStatus.empty);
        }
    }

    public get src(): string {
        return this._imgAddr;
    }

    public get fileEdit(): FileEditElement {
        return this._fileEdit;
    }
    //#endregion Properties

    public setStatus(status: FileEditStatus): void {
        this._status = status;
        this.classList.remove("Error", "Uploading", "Empty", "Download", "Finishing", "NotImage");

        if (this._status == FileEditStatus.empty) {
            this.classList.add("Empty");
        } else if (this._status == FileEditStatus.error)
            this.classList.add("Error");
        else if (this._status == FileEditStatus.previewNotSupported) {
            this.classList.add("Error", "NotImage");
        } else if (this._status == FileEditStatus.uploading) {
            this.classList.add("Uploading");
        } else if (this._status == FileEditStatus.download) {
            this.classList.add("Download");
        }
    }

    /**
     * Vytvori velké náhledové okno
     */
    private _createImageWindow(): void {
        const imgWindow = createElement({ classes: "fullImagePreview" });
        const prevWindow = createElement({ classes: "previewContainer" });
        const image = createElement({ type: "img", classes: "image", value: this.src }) as HTMLImageElement;
        const toolbarImg = createElement({ classes: "toolbar" });
        const btn = createElement({ classes: "closeBtn" });
        let windowKeyEvent: any;

        const f_unregister = () => {
            GenericControl.unregisterEventListener(btn);
            GenericControl.unregisterEventListener(prevWindow);
            window.removeEventListener("keyup", windowKeyEvent);
        };
        toolbarImg.appendChild(btn);

        imgWindow.appendChild(toolbarImg);
        imgWindow.appendChild(prevWindow);
        imgWindow.appendChild(image);

        GenericControl.registerEventListener(btn, "click", () => {
            f_unregister();
            imgWindow.remove();
        });

        GenericControl.registerEventListener(prevWindow, "click", () => {
            f_unregister();
            imgWindow.remove();
        });

        window.addEventListener("keyup", windowKeyEvent = (e: KeyboardEvent) => {
            if (e.keyCode == 27) { //ESC
                f_unregister();
                imgWindow.remove();
            }
        });

        document.body.appendChild(imgWindow);
    }

    constructor(self?: any) {
        super();
        self ? self._constructor() : this._constructor(); // polyfill
    }

    protected _constructor(): void {
        GenericControl._constructor(this as HTMLElement);
        this.classList.add("ImageEditElement");
        this._errorSrc = false;
        this._fileEdit = new FileEditElement();
        this.setStatus(FileEditStatus.empty);
        this.enabled = false;

        this._previewContainer = createElement({ classes: "previewContainer" });
        const toolbarImg = createElement({ classes: "toolbar" });
        this._imageElement = createElement({ type: "div", classes: "imagePreview" }) as HTMLImageElement;
        this._imageTestElement = document.createElement("img");
        this._resizeBtn = createElement({ classes: "resizeBtn" });
        const noimagespan = createElement({ type: "span", classes: "errorText" });

        Localization.getTextPromise("GUI-601172").then((r) => {
            noimagespan.textContent = r;
        });

        this._fileEdit.mask = "image/*";

        toolbarImg.appendChild(this._resizeBtn);

        this._previewContainer.appendChild(toolbarImg);
        this._previewContainer.appendChild(this._imageElement);
        this._previewContainer.appendChild(noimagespan);

        this.appendChild(this._previewContainer);
        this.appendChild(this._fileEdit);

        this._fileEdit.dropzone = this._previewContainer;
    }

    protected connectedCallback(): void {
        (this._fileEdit as SimpleFileEditElement).registerEventListener("uploadRequest", (e) => {
            if (e.detail.files.length > 0) {
                if (e.detail.files[0].size < 15965008) {
                    const fr = new FileReader();
                    fr.onload = (ex: any) => {
                        if (ex.loaded && ex.loaded < 15965008)
                            this.src = ex.target.result;
                        this.setStatus(FileEditStatus.uploading);
                    };
                    fr.readAsDataURL(e.detail.files[0]);
                } else {
                    this.setStatus(FileEditStatus.uploading);
                }
            }
        });

        this._fileEdit.registerEventListener("statusChange", (e: IFileUploadStatusChangeEvent) => {
            if (this._errorSrc && e.detail.status == FileEditStatus.download)
                this.setStatus(FileEditStatus.error);
            else
                this.setStatus(e.detail.status);
        });

        GenericControl.registerEventListener(this._previewContainer, "click", (e: MouseEvent) => {
            EventHelper.stopPropagation(e);
            if (this._status == FileEditStatus.download)
                this._createImageWindow();
        });

        GenericControl.registerEventListener(this._imageElement, "error", (_e) => {
            this.setStatus(FileEditStatus.previewNotSupported);
        });

        GenericControl.registerEventListener(this._imageTestElement, "error", (_e) => {
            this.setStatus(FileEditStatus.previewNotSupported);
            this._errorSrc = true;
        });
    }

    protected disconnectedCallback(): void {
        this.unregisterEventListener();
        GenericControl.unregisterEventListener(this._previewContainer);
        GenericControl.unregisterEventListener(this._imageElement);
    }

    protected attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        GenericControl.attributeChangedCallback(this as HTMLElement, attrName, oldVal, newVal);
    }

    //#region Zpracování metod z globální třídy GenericControl
    /**
     * Zaregistruje událost
     * @param event Název události
     * @param action Akce při vyvolání události
     */
    public registerEventListener(event: string, action: (e: Event) => void): void {
        GenericControl.registerEventListener(this as HTMLElement, event, action);
    }

    /**
     * Odregistruje udalost. Pokud neni vyplnen zadny parametr, jsou odregistrovany vsechny udalosti. Pokud je vyplnen pouze event, odregistruje vsechny udalosti daneho typu
     * pokud je vyplnen event a action, odregistruje pouze konkretni akci
     * @param event Event, ktery chcete odregistrovat.
     * @param action Akce, ktere se tyka event a chcete ji odregistrovat
     */
    public unregisterEventListener(event?: string, action?: (e: Event) => void): void {
        GenericControl.unregisterEventListener(this as HTMLElement, event, action);
    }

    /**
     * Zavolá událost na elementu
     * @param eventName Název události
     * @param detail Podrobnosti události
     * @return Vrátí customEvent
     */
    public callEvent(eventName: string, detail?: any, bubbles: boolean = false, cancelable: boolean = false): CustomEvent {
        return GenericControl.callEvent(this as HTMLElement, eventName, detail, bubbles, cancelable);
    }

    /**
     * Vrátí pozici elementu v rámci dokumentu
     */
    public offset(): { top: number; left: number } {
        return GenericControl.offset(this as HTMLElement);
    }

    //#enregion Zpracování metod z globální třídy GenericControl
}

registerCustomElement("tsc-ImageEdit", ImageEditElement);
